import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { AuthService } from '@app/core/auth/auth.service';
import { ApiService } from '@app/core/services/api.service';
import { NGXLogger } from 'ngx-logger';

/**
 * OTP screen (second factor). Reached after the first factor succeeded but the account has 2FA enabled
 * and the session is not yet verified (see AuthService.beginMfaChallenge). On success the session is
 * marked verified server-side and the original redirect is continued.
 *
 * Must work inside embedded browsers (CC Edge webview, CB3 webview): only standard inputs, inputmode
 * numeric, autofocus — no exotic browser APIs.
 */
@Component({
  selector: 'app-mfa',
  templateUrl: './mfa.component.html',
  styleUrls: ['./mfa.component.scss']
})
export class MfaComponent implements AfterViewInit {

  public code = '';
  public isLoading = false;
  public codeError = false;

  @ViewChild('codeInput') private codeInput?: ElementRef<HTMLInputElement>;

  constructor(
    private auth: AuthService,
    private api: ApiService,
    private log: NGXLogger
  ) { }

  ngAfterViewInit(): void {
    this.focusInput();
  }

  onCodeInput() {
    // Keep only digits, max 6 (webview-safe)
    this.code = (this.code ?? '').replace(/\D/g, '').slice(0, 6);
    this.codeError = false;
    // Auto-submit once all 6 digits are entered.
    if (this.code.length === 6) {
      this.onSubmit();
    }
  }

  onSubmit() {
    if (this.isLoading) {
      return;
    }
    const code = (this.code ?? '').trim();
    if (code.length < 6) {
      this.codeError = true;
      return;
    }

    this.isLoading = true;
    this.codeError = false;

    this.api.mfaVerify(code).subscribe({
      next: () => {
        this.isLoading = false;
        this.log.info('MFA verify successful');
        this.auth.completeMfaChallenge();
      },
      error: () => {
        this.isLoading = false;
        this.codeError = true;
        this.code = '';
        this.focusInput();
      }
    });
  }

  onCancel() {
    this.auth.cancelMfaChallenge();
  }

  private focusInput() {
    setTimeout(() => this.codeInput?.nativeElement.focus(), 0);
  }
}
